import { rgba } from "polished";
import PropTypes from "prop-types"; // Importing PropTypes for type checking
import React from "react";
import { IoClose } from "react-icons/io5";
import styled, { css, keyframes } from "styled-components";
import { theme } from "../theme";
import { Button } from "./button";
import { FabButton } from "./fab-button";
import { Box, Flex } from "./layout";
import { Typography } from "./typography";

// Define styles for different sizes
const sizeStyles = {
  xs: {
    maxWidth: '25rem',
  },
  sm: {
    maxWidth: '37.5rem',
  },
  md: {
    maxWidth: '50rem',
  },
  lg: {
    maxWidth: '62.5rem',
  },
  xl: {
    maxWidth: '75rem',
  },
  xxl: {
    maxWidth: '87 .5rem',
  },
};

const DialogWrapper = styled(Box)(() => ({
  position: "fixed",
  width: "100%",
  height: "100%",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  zIndex: 50,
  "& *":{boxSizing: "border-box"}
}));

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const DialogBg = styled(Box)(() => css`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 10;
  background-color: ${rgba(theme.colors.dark.dark, 0.65)};
  animation: ${fadeIn} 0.3s ease-out;
  overflow: hidden;
`);

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -25%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const DialogBox = styled(Flex)(({ maxWidth,fullWidth,fullPage}) => css`
  z-index: 21;
  background-color: ${theme.colors.light.light};
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  min-width: 18.75rem;
  border-radius: ${!fullPage && theme.radius.card};
  box-shadow: 0px 0 1.25rem ${rgba(theme.colors.dark.dark, 0.15)};
  animation: ${slideIn} 0.2s ease-out;
  width: ${fullPage ? "100%" : fullWidth ? "90%" : 'auto'};
  max-width: ${fullPage ? "100%" : sizeStyles[maxWidth]?.maxWidth || "90%"};
  height: ${fullPage && "100%"};
    &.fullPage {
      & ${DialogBody} {
        height:100%;
      }
    }
`);

const DialogClose = styled(Flex)(({ hasTitle }) => ({
  width: "2.5rem",
  height: "auto",
  position: !hasTitle &&  "absolute",
  right: "0.5rem",
  top: "0.5rem",
  zIndex: 50,
}));

const DialogHeader = styled(Flex)(({closeButton,separator}) => ({
  paddingBlockStart: "0.9375rem",
  paddingInline: closeButton ? "1.25rem 0.625rem" : "1.25rem",
  width: "100%",
  zIndex: 50,
  ...(separator && {
    borderBlockEnd: `${theme.colors.default.main} solid 0.0625rem`,
    paddingBlockEnd: "0.9375rem",
  }),
}));

const DialogAction = styled(Flex)(({actionAllign,separator}) => ({
  paddingBlockEnd: "0.9375rem",
  paddingInline: "1.25rem",
  width: "100%",
  zIndex: 50,
  alignItems: "center",
  gap:"0.625rem",
  justifyContent: actionAllign || "flex-end",
  ...(separator && {
    borderBlockStart: `${theme.colors.default.main} solid 0.0625rem`,
    paddingBlockStart: "0.9375rem",
  }),
}));

const DialogBody = styled(Box)(({fullPage,bodyScroll,noPadding}) => ({
  padding:noPadding? 0: "1.25rem",
  width: "100%",
  height: fullPage ? "100%" : "auto",
  maxHeight: bodyScroll && "calc(100vh - 12rem)",
  overflow:bodyScroll && "auto",
  zIndex: 51,
}));


export const Dialog = ({
  title,
  secondary,
  header = true,
  children,
  closeButton = true,
  action = true,
  maxWidth = "xs",
  fullWidth = false,
  fullPage = false,
  actionAllign,
  open, 
  onClose,
  onSubmit,
  bgClose,
  separator,
  footer,
  buttonName,
  btnLoading,
  bodyScroll,
  noPadding,
  btnDisable=false
}) => {
  if (!open) return null; 
  return (
    <>
      {open && 
        <DialogWrapper>
          <DialogBg  {...(bgClose && { onClick: onClose })}/>
          <DialogBox 
            maxWidth={maxWidth} 
            fullWidth={fullWidth}
            fullPage={fullPage}
            direction = {"column"}
            className={fullPage && "fullPage"}
          >
            {header && (
              <DialogHeader 
                alignItem="center"
                justifyContent="space-between"
                closeButton={closeButton}
                separator={separator}
              >
                <Box>
                {title && 
                  <Typography mb="0" variant="h5">
                    {title}
                  </Typography>
                }
                {secondary && 
                 <Typography variant="body2" mb={"0"}>
                  {secondary}
                </Typography>
                }
                </Box>
                {closeButton && (
                  <DialogClose hasTitle={true}>
                    <FabButton icon={<IoClose />} onClick={onClose} size="sm" />
                  </DialogClose>
                )}
              </DialogHeader>
            )}
            {!header && closeButton && (
              <DialogClose hasTitle={false}>
                <FabButton icon={<IoClose />} onClick={onClose} size="sm"/>
              </DialogClose>
            )}
            
            <DialogBody bodyScroll={bodyScroll} noPadding={noPadding}>{children}</DialogBody>
            {(action || footer) && 
              <DialogAction actionAllign={actionAllign} separator={separator}>
                {footer && 
                  {...footer}
                }
                {closeButton && (<Button onClick={onClose} variant="outlined">
                Cancel
                </Button>)}
                <Button color="primary" onClick={onSubmit} variant="contained" loading={btnLoading} disabled={btnDisable}>
                  {buttonName || "Submit"}
                </Button>
              </DialogAction>
            }
            
          </DialogBox>
        </DialogWrapper>
      }
    </>
  );
}
// Set default props
Dialog.defaultProps = {
  title: "",                    // Default title is an empty string if not provided
  children: null,               // Children are required in propTypes, so this will typically not be undefined
  closeButton: true,            // Shows close button by default
  action: true,                 // Action section (footer) is shown by default
  maxWidth: "xs",               // Default max width for the dialog is 'xs'
  fullWidth: false,             // Dialog is not full width by default
  fullPage: false,              // Dialog is not full page by default
  actionAllign: "flex-end",     // Action alignment defaults to 'flex-end'
  open: false,                  // Dialog is closed by default
  onClose: () => {},            // Default onClose is a no-op function to prevent errors if not passed
  onSubmit: () => {},           // Default onSubmit is a no-op function
  bgClose: false,               // Clicking background does not close dialog by default
  separator: false,             // Separator is not shown by default
  footer: null,                 // No footer content by default
};

// Define prop types
Dialog.propTypes = {
  title: PropTypes.string,                            // Title is an optional string
  children: PropTypes.node.isRequired,                // Children are required and must be valid React nodes
  closeButton: PropTypes.bool,                        // Whether to show a close button
  action: PropTypes.bool,                             // Whether to show action buttons
  maxWidth: PropTypes.oneOf(Object.keys(sizeStyles)), // Ensures it matches one of the defined sizes
  fullWidth: PropTypes.bool,                          // Full width dialog toggle
  fullPage: PropTypes.bool,                           // Full page dialog toggle
  actionAllign: PropTypes.oneOf(['flex-start', 'center', 'flex-end' ,'space-between', 'space-around', 'space-evenly']), // Aligns the action buttons
  open: PropTypes.bool,                               // Open/closed state of the dialog
  onClose: PropTypes.func.isRequired,                 // onClose handler is required
  onSubmit: PropTypes.func,                           // onSubmit handler is optional
  bgClose: PropTypes.bool,                            // Toggle for closing dialog on background click
  separator: PropTypes.bool,                          // Toggle for showing separator line
  footer: PropTypes.node,                             // Custom footer content, can be any valid React node
};
